import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { studentService } from '../../services/studentService';
import { tutorService } from '../../services/tutorService';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, Loader2, UserPlus } from 'lucide-react';

const AddStudent = () => {
  const [formData, setFormData] = useState({
    name: '',
    class_id: '',
    board_id: '',
  });
  const [classes, setClasses] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingMasterData, setFetchingMasterData] = useState(true);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    setFetchingMasterData(true);
    try {
      const [classesRes, boardsRes] = await Promise.all([
        tutorService.getClasses(),
        tutorService.getBoards(),
      ]);
      setClasses(classesRes.data || classesRes || []);
      setBoards(boardsRes.data || boardsRes || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load form data',
        variant: 'destructive',
      });
    } finally {
      setFetchingMasterData(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Student name is required';
    }
    if (!formData.class_id) {
      newErrors.class_id = 'Please select a class';
    }
    if (!formData.board_id) {
      newErrors.board_id = 'Please select a board';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await studentService.createStudent({
        name: formData.name.trim(),
        class_id: formData.class_id,
        board_id: formData.board_id,
      });
      
      if (response.success || response.data) {
        toast({
          title: 'Success',
          description: 'Student added successfully',
        });
        navigate('/customer/students');
      }
    } catch (error) {
      const responseData = error.response?.data;
      
      if (responseData?.errors) {
        const apiErrors = {};
        Object.keys(responseData.errors).forEach(key => {
          apiErrors[key] = responseData.errors[key][0];
        });
        setErrors(prev => ({ ...prev, ...apiErrors }));
        toast({
          title: 'Validation Error',
          description: Object.values(responseData.errors)[0][0] || 'Please check the form',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: responseData?.message || 'Failed to add student',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  if (fetchingMasterData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-xl mx-auto">
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/customer/students')}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Students
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Add New Student
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Student Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="Enter student name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`transition-all duration-200 focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                />
                {errors.name && (
                  <p className="text-sm text-red-500" role="alert">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="class">
                  Class <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.class_id}
                  onValueChange={(value) => handleInputChange('class_id', value)}
                >
                  <SelectTrigger id="class" className={`transition-all duration-200 focus:ring-2 focus:ring-blue-500 ${errors.class_id ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={String(cls.id)}>
                        {cls.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.class_id && (
                  <p className="text-sm text-red-500" role="alert">{errors.class_id}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="board">
                  Board <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.board_id}
                  onValueChange={(value) => handleInputChange('board_id', value)}
                >
                  <SelectTrigger id="board" className={`transition-all duration-200 focus:ring-2 focus:ring-blue-500 ${errors.board_id ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Select board" />
                  </SelectTrigger>
                  <SelectContent>
                    {boards.map((board) => (
                      <SelectItem key={board.id} value={String(board.id)}>
                        {board.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.board_id && (
                  <p className="text-sm text-red-500" role="alert">{errors.board_id}</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/customer/students')}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600">
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Student'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddStudent;
